import { Star, StarHalf, Heart } from "lucide-react";
import { cn } from "@/lib/utils";

interface RatingHeartDisplayProps {
  rating?: number | null;
  liked?: boolean | null;
  size?: number;
  starClassName?: string;
  heartClassName?: string;
  className?: string;
  gapClassName?: string;
}

/**
 * Compact inline display of a star rating with an optional purple heart
 * indicating the member "liked this show". Heart-only, stars-only, and
 * combined renderings are all supported. Renders nothing when both signals
 * are absent.
 */
const RatingHeartDisplay = ({
  rating,
  liked,
  size = 14,
  starClassName = "text-yellow-500 fill-yellow-500",
  heartClassName = "text-primary fill-current",
  className,
  gapClassName = "gap-1.5",
}: RatingHeartDisplayProps) => {
  const value = Math.max(0, Math.min(5, Number(rating) || 0));
  const fullStars = Math.floor(value);
  const hasHalf = value % 1 === 0.5;
  const hasRating = value > 0;

  if (!hasRating && !liked) return null;

  return (
    <span className={cn("inline-flex items-center", gapClassName, className)}>
      {hasRating && (
        <span className="inline-flex items-center gap-0.5">
          {Array.from({ length: fullStars }).map((_, i) => (
            <Star key={i} size={size} className={starClassName} />
          ))}
          {hasHalf && <StarHalf size={size} className={starClassName} />}
        </span>
      )}
      {liked && (
        <Heart
          size={size}
          className={heartClassName}
          aria-label="Liked this show"
        />
      )}
    </span>
  );
};

export default RatingHeartDisplay;
